import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BarChart3, Check, CheckCircle2, Info, Loader2, RotateCcw, Rocket, Search, X } from "lucide-react";
import { useWizardT } from "@/content/wizard";
import { startPublicScan } from "@/lib/api";

const inputClass = "mt-1.5 w-full rounded-xl border border-[#E8ECF1] bg-white px-4 py-3 text-sm text-[#0F172A] placeholder:text-[#9AA7B2] focus-visible:border-[#3B82F6] focus-visible:ring-2 focus-visible:ring-[#3B82F6]/25 focus-visible:outline-none";
const primaryBtn = "inline-flex items-center justify-center gap-2 rounded-xl bg-[#1F2937] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#111827] focus-visible:ring-2 focus-visible:ring-[#1F2937] focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-60";
const ghostBtn = "inline-flex items-center justify-center gap-2 rounded-xl border border-[#E8ECF1] bg-white px-6 py-3.5 text-sm font-semibold text-[#475569] transition-colors hover:bg-[#FAFBFC] focus-visible:ring-2 focus-visible:ring-[#3B82F6] focus-visible:ring-offset-2 focus-visible:outline-none";
const errText = "mt-1.5 text-xs text-[#EF4444]";
const labelClass = "text-sm font-medium text-[#475569]";
const emailOk = v => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

function Stepper({ steps, current }) {
  return <ol className="flex flex-wrap items-start justify-center gap-x-6 gap-y-4 sm:gap-x-10">
      {steps.map((label, i) => {
      const done = i < current;
      const active = i === current;
      return <li key={label} className="flex w-24 flex-col items-center gap-2 text-center sm:w-32">
            <span className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold ${done ? "border-transparent bg-[#22C55E] text-white" : active ? "border-[#0F172A] bg-white text-[#0F172A]" : "border-[#E8ECF1] bg-white text-[#CBD5E1]"}`}>
              {done ? <Check size={16} aria-hidden /> : i + 1}
            </span>
            <span className={`text-[11px] leading-tight ${active ? "font-semibold text-[#0F172A]" : "text-[#94A3B8]"}`}>
              {label}
            </span>
          </li>;
    })}
    </ol>;
}

function Card({ children }) {
  return <div className="mt-10 rounded-3xl border border-[#E8ECF1] bg-white p-6 shadow-sm sm:p-8">
      {children}
    </div>;
}

// Real steps only (no email-OTP step — user decision 2026-09-09: no
// email-sending infrastructure exists yet, and the report is shown
// synchronously on this page instead of being emailed).
const STEP_BUSINESS = 0;
const STEP_SERVICE = 1;
const STEP_CONTACT = 2;

function formatReusedDate(v) {
  const d = typeof v === "string" ? new Date(v) : null;
  return d && !isNaN(d) ? d.toLocaleDateString() : "";
}

export function AnalysisWizard() {
  const w = useWizardT();
  const [step, setStep] = useState(STEP_BUSINESS);
  // "form" | "running" | "done" | "error"
  const [phase, setPhase] = useState("form");
  const [busy, setBusy] = useState(false);
  const [reportToken, setReportToken] = useState(null);
  const [scanError, setScanError] = useState("");
  const [reusedAt, setReusedAt] = useState(null);

  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [website, setWebsite] = useState("");
  const [term, setTerm] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const setError = (k, v) => setErrors(p => {
    const n = { ...p };
    if (v) n[k] = v; else delete n[k];
    return n;
  });

  // Ambient "what we're doing" text while the real request is in flight —
  // purely cosmetic (the real audit does the same work regardless of what
  // this says), but it cycles only as long as the request actually takes,
  // it never fakes completion on a fixed timer the way the original mock
  // did. See onStart: the phase only becomes "done" once the real response
  // arrives.
  const [stage, setStage] = useState(0);
  const runningRef = useRef(false);
  useEffect(() => {
    if (phase !== "running") return;
    runningRef.current = true;
    const id = window.setInterval(() => {
      setStage(s => (runningRef.current ? Math.min(s + 1, w.running.stages.length - 1) : s));
    }, 1400);
    return () => {
      runningRef.current = false;
      window.clearInterval(id);
    };
  }, [phase, w.running.stages.length]);

  const reset = () => {
    setStep(STEP_BUSINESS);
    setPhase("form");
    setReportToken(null);
    setReusedAt(null);
    setScanError("");
    setCompany("");
    setCity("");
    setWebsite("");
    setTerm("");
    setSelectedTerm("");
    setContactName("");
    setEmail("");
    setPhone("");
    setConsent(false);
    setErrors({});
    setStage(0);
  };

  const nextFromBusiness = () => {
    const next = {};
    if (!company.trim()) next["company"] = w.business.errors.name;
    if (!city.trim()) next["city"] = w.business.errors.city;
    setErrors(next);
    if (Object.keys(next).length === 0) setStep(STEP_SERVICE);
  };

  const nextFromService = () => {
    const chosen = selectedTerm || term.trim();
    if (!chosen) return setError("term", w.service.errors.term);
    setSelectedTerm(chosen);
    setError("term");
    setStep(STEP_CONTACT);
  };

  const onStart = async () => {
    const next = {};
    if (!contactName.trim()) next["contactName"] = w.contact.errors.name;
    if (!emailOk(email)) next["email"] = w.contact.errors.email;
    if (!consent) next["consent"] = w.contact.errors.consent;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    setStage(0);
    setPhase("running");
    try {
      const res = await startPublicScan({
        company: company.trim(),
        city: city.trim(),
        website: website.trim() || undefined,
        search_term: selectedTerm,
        contact_name: contactName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        consent: true,
      });
      setReportToken(res.token);
      setReusedAt(res.reused ? (res.analysed_at || true) : null);
      setPhase("done");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || w.running.error;
      setScanError(typeof msg === "string" ? msg : w.running.error);
      setPhase("error");
    } finally {
      setBusy(false);
    }
  };

  return <section className="bg-[#FAFBFC] pt-28 pb-24 sm:pt-32">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.14em] text-[#94A3B8] uppercase">
              Positivup
            </p>
            <h1 className="font-display mt-2 text-2xl font-bold text-[#0F172A] sm:text-3xl">
              {w.pageTitle}
            </h1>
          </div>
          <button type="button" onClick={reset} className="mt-1 inline-flex items-center gap-2 text-xs font-semibold text-[#94A3B8] transition-colors hover:text-[#0F172A]">
            <RotateCcw size={14} aria-hidden />
            {w.restart}
          </button>
        </div>

        <div className="mt-8">
          <Stepper steps={w.steps} current={phase === "form" ? step : w.steps.length} />
        </div>

        {phase === "running" && <Card>
            <div className="flex items-center gap-3">
              <Loader2 size={20} className="animate-spin text-[#3B82F6]" aria-hidden />
              <h2 className="font-display text-lg font-semibold text-[#0F172A]">
                {w.running.title}
              </h2>
            </div>
            <p className="mt-3 text-sm text-[#64748B]">{w.running.note}</p>
            <p className="mt-6 text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
              {w.running.stageLabel.replace("{current}", String(stage + 1)).replace("{total}", String(w.running.stages.length))}
            </p>
            <p className="mt-1.5 text-sm text-[#0F172A]">{w.running.stages[stage]}</p>
            <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-[#E8ECF1]">
              <div className="h-full rounded-full bg-[#3B82F6] transition-all duration-700" style={{
            width: `${(stage + 1) / w.running.stages.length * 100}%`
          }} />
            </div>
            <p className="mt-3 text-xs text-[#94A3B8]">{w.running.processing}</p>
          </Card>}

        {phase === "error" && <Card>
            <div className="rounded-2xl border border-[#EF4444]/30 bg-[#EF4444]/5 p-6 text-center">
              <AlertTriangle size={34} className="mx-auto text-[#EF4444]" aria-hidden />
              <h2 className="font-display mt-3 text-lg font-semibold text-[#0F172A]">
                {w.running.errorTitle}
              </h2>
              <p className="mt-2 text-sm text-[#64748B]">{scanError}</p>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setPhase("form")} className={`${primaryBtn} flex-1`}>
                {w.running.retry}
              </button>
              <button type="button" onClick={reset} className={`${ghostBtn} flex-1`}>
                <RotateCcw size={16} aria-hidden />
                {w.restart}
              </button>
            </div>
          </Card>}

        {phase === "done" && <Card>
            <div className="rounded-2xl border border-[#22C55E]/30 bg-[#22C55E]/5 p-6 text-center">
              <CheckCircle2 size={34} className="mx-auto text-[#22C55E]" aria-hidden />
              <h2 className="font-display mt-3 text-lg font-semibold text-[#0F172A]">
                {w.done.title}
              </h2>
              <p className="mt-2 text-sm text-[#64748B]">{w.done.body}</p>
            </div>
            {reusedAt && <p role="status" className="mt-5 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/5 p-4 text-sm text-[#0F172A]">
                {w.done.reusedNotice.replace("{date}", formatReusedDate(reusedAt))}
              </p>}
            <p className="mt-5 text-xs leading-relaxed text-[#94A3B8]">{w.done.pendingNote}</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to={`/rapor/${reportToken}`} className={`${primaryBtn} flex-1`}>
                <BarChart3 size={18} aria-hidden />
                {w.done.view}
              </Link>
              <button type="button" onClick={reset} className={`${ghostBtn} flex-1`}>
                <RotateCcw size={16} aria-hidden />
                {w.restart}
              </button>
            </div>
          </Card>}

        {phase === "form" && step === STEP_BUSINESS && <Card>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">
              {w.business.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{w.business.lead}</p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="wz-company" className={labelClass}>
                  {w.business.name} *
                </label>
                <input id="wz-company" value={company} onChange={e => {
              setCompany(e.target.value);
              setError("company");
            }} placeholder={w.business.namePlaceholder} className={inputClass} />
                {errors["company"] && <p className={errText}>{errors["company"]}</p>}
              </div>
              <div>
                <label htmlFor="wz-city" className={labelClass}>
                  {w.business.city} *
                </label>
                <input id="wz-city" value={city} onChange={e => {
              setCity(e.target.value);
              setError("city");
            }} placeholder={w.business.cityPlaceholder} className={inputClass} />
                {errors["city"] && <p className={errText}>{errors["city"]}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="wz-website" className={labelClass}>
                  {w.business.website} <span className="text-[#94A3B8]">({w.optional})</span>
                </label>
                <input id="wz-website" value={website} onChange={e => setWebsite(e.target.value)} placeholder={w.business.websitePlaceholder} className={inputClass} />
              </div>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={nextFromBusiness} className={`${primaryBtn} flex-1`}>
                {w.next}
              </button>
            </div>
          </Card>}

        {phase === "form" && step === STEP_SERVICE && <Card>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">{w.service.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{w.service.lead}</p>

            <label htmlFor="wz-term" className={`${labelClass} mt-6 block`}>
              {w.service.label} *
            </label>
            <div className="flex gap-3">
              <input id="wz-term" value={term} onChange={e => {
            setTerm(e.target.value);
            setError("term");
          }} placeholder={w.service.placeholder} className={inputClass} />
              <button type="button" onClick={() => {
            if (!term.trim()) return setError("term", w.service.errors.term);
            setSelectedTerm(term.trim());
            setError("term");
          }} className={`${primaryBtn} mt-1.5 shrink-0 px-5`}>
                {w.service.select}
              </button>
            </div>
            {errors["term"] && <p className={errText}>{errors["term"]}</p>}

            <div className="mt-6 flex items-baseline justify-between gap-4">
              <p className="text-sm text-[#64748B]">{w.service.suggestionsLabel}</p>
              <p className="text-xs text-[#94A3B8]">{w.service.suggestionsNote}</p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {w.service.suggestions.map(s => <button key={s} type="button" onClick={() => {
            setSelectedTerm(s);
            setTerm(s);
            setError("term");
          }} className={`rounded-full border px-4 py-2 text-sm transition-colors ${selectedTerm === s ? "border-[#3B82F6] bg-[#3B82F6]/5 font-semibold text-[#1D4ED8]" : "border-[#E8ECF1] bg-white text-[#475569] hover:bg-[#FAFBFC]"}`}>
                  {s}
                </button>)}
            </div>

            {selectedTerm && <div className="mt-6 flex items-center gap-3 rounded-2xl border border-[#E8ECF1] bg-[#FAFBFC] p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#3B82F6]">
                  <Search size={16} aria-hidden />
                </span>
                <div className="flex-1">
                  <p className="text-xs text-[#94A3B8]">{w.service.selectedLabel}</p>
                  <p className="font-display text-base font-semibold text-[#0F172A]">
                    „{selectedTerm}“
                  </p>
                </div>
                <button type="button" aria-label={w.service.clear} onClick={() => setSelectedTerm("")} className="text-[#94A3B8] transition-colors hover:text-[#0F172A]">
                  <X size={18} aria-hidden />
                </button>
              </div>}

            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[#E8ECF1] bg-white p-4">
              <Info size={16} className="mt-0.5 shrink-0 text-[#94A3B8]" aria-hidden />
              <p className="text-xs leading-relaxed text-[#64748B]">{w.service.info}</p>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setStep(STEP_BUSINESS)} className={`${ghostBtn} flex-1`}>
                {w.back}
              </button>
              <button type="button" onClick={nextFromService} className={`${primaryBtn} flex-1`}>
                {w.next}
              </button>
            </div>
          </Card>}

        {phase === "form" && step === STEP_CONTACT && <Card>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">{w.contact.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{w.contact.lead}</p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="wz-name" className={labelClass}>
                  {w.contact.name} *
                </label>
                <input id="wz-name" value={contactName} onChange={e => {
              setContactName(e.target.value);
              setError("contactName");
            }} placeholder={w.contact.namePlaceholder} className={inputClass} />
                {errors["contactName"] && <p className={errText}>{errors["contactName"]}</p>}
              </div>
              <div>
                <label htmlFor="wz-email" className={labelClass}>
                  {w.contact.email} *
                </label>
                <input id="wz-email" type="email" value={email} onChange={e => {
              setEmail(e.target.value);
              setError("email");
            }} placeholder={w.contact.emailPlaceholder} className={inputClass} />
                {errors["email"] && <p className={errText}>{errors["email"]}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="wz-phone" className={labelClass}>
                  {w.contact.phone} <span className="text-[#94A3B8]">({w.optional})</span>
                </label>
                <input id="wz-phone" value={phone} onChange={e => setPhone(e.target.value)} placeholder={w.contact.phonePlaceholder} className={inputClass} />
              </div>
            </div>

            <div className="mt-6 flex items-start gap-3">
              <input id="wz-consent" type="checkbox" checked={consent} onChange={e => {
            setConsent(e.target.checked);
            setError("consent");
          }} className="mt-1 h-4 w-4 shrink-0 accent-[#3B82F6]" />
              <label htmlFor="wz-consent" className="text-sm leading-relaxed text-[#64748B]">
                {w.contact.consent}{" "}
                <Link to="/datenschutz" className="font-medium text-[#3B82F6] underline">
                  {w.contact.consentLink}
                </Link>
              </label>
            </div>
            {errors["consent"] && <p className={errText}>{errors["consent"]}</p>}

            <div className="mt-8 rounded-2xl border border-[#E8ECF1] bg-[#FAFBFC] p-5">
              <p className="font-display text-sm font-semibold text-[#0F172A]">
                {w.summary.heading}
              </p>
              <dl className="mt-3 grid gap-2 text-sm">
                {[[w.summary.business, company.trim()], [w.summary.city, city.trim()], [w.summary.term, selectedTerm ? `„${selectedTerm}“` : "—"], [w.summary.email, email.trim()]].map(([k, v]) => <div key={k} className="flex items-baseline justify-between gap-4">
                    <dt className="text-[#94A3B8]">{k}</dt>
                    <dd className="text-right font-medium text-[#0F172A]">{v || "—"}</dd>
                  </div>)}
              </dl>
              <p className="mt-4 text-xs leading-relaxed text-[#94A3B8]">{w.summary.lead}</p>
            </div>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <button type="button" onClick={() => setStep(STEP_SERVICE)} className={`${ghostBtn} flex-1`}>
                {w.back}
              </button>
              <button type="button" onClick={onStart} disabled={busy} className={`${primaryBtn} flex-1`}>
                <Rocket size={18} aria-hidden />
                {w.summary.start}
              </button>
            </div>
          </Card>}
      </div>
    </section>;
}
